import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import cn from 'clsx'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { validateSemiForm } from 'lib/formUtils'
import { isBedbank, isBundleOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMaxChildAge,
  getTourV2OfferMinAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
} from 'lib/offer/offerUtils'
import { countOccupantsForProperty, generateRoomOccupants, countOccupantsForBundleAndSave } from 'lib/offer/occupancyUtils'
import { getInteractionStudioDataFromOfferPage } from 'selectors/interactionStudioSelectors'
import { interactionStudioAPIController } from 'lib/interactionStudioUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import config from 'constants/config'

interface Props {
  rooms?: Array<App.RoomOccupants>;
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer;
  open?: boolean;
  capacities?: Array<App.PackageRoomCapacity>;
  disclaimer?: string;
  interactionStudioData: App.ISACheckoutData
  childrenAgeLabel?: string
  className?: string;
  filter: OfferPageFilterTypes;
  onApply?: () => void
  enableMultiRoomTypesEvents?: boolean;
  bundleOfferId?: string;
  disableMultiRoom?: boolean;
}

const defaultRooms = [generateRoomOccupants()]

function OfferCapacityWithAgesSelection(props: Props) {
  const {
    rooms = defaultRooms,
    offer,
    open,
    interactionStudioData,
    capacities,
    disclaimer,
    childrenAgeLabel = 'at time of check-in',
    className,
    filter,
    onApply,
    enableMultiRoomTypesEvents,
    bundleOfferId,
    disableMultiRoom = !config.accommodation.allowsMultiRoom,
  } = props
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const pickerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (pickerRef.current) {
      if (open) {
        validateSemiForm(pickerRef.current)
      }
    }
  }, [open])

  const onGuestChange = useCallback((nextRooms: Array<App.RoomOccupants>) => {
    if (isBedbank(offer) || isTourV2Offer(offer)) {
      dispatch({ type: OfferPageActions.setRooms, rooms: nextRooms })
    } else {
      dispatch({
        type: OfferPageActions.setRooms,
        rooms: nextRooms.map(({ roomId, ...rest }) => ({
          ...(isBundleOffer(offer) ? countOccupantsForBundleAndSave([rest], offer) : countOccupantsForProperty([rest], offer.property)),
          roomId,
        })),
      })
    }
  }, [offer, dispatch])

  const handleApply = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
    interactionStudioAPIController(filter, interactionStudioData)
    onApply?.()
    trackEvent('OCCUPANCY_FILTERS_APPLIED')
  }, [dispatch, filter, interactionStudioData, onApply, trackEvent])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbank(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      if (offer.isLeTour) {
        return getTourV2OfferMinAge(offer)
      }
      return getTourV2OfferMaxChildAge(offer)
    } else if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && offer.isLeTour), [offer])

  const infantAgeThreshold = useMemo(() => {
    if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  return (
    <OccupancyPicker
      ref={pickerRef}
      bundleOfferId={bundleOfferId}
      occupancies={rooms}
      saleUnit={offer.saleUnit}
      onChange={onGuestChange}
      onApply={handleApply}
      maxChildAge={childrenAgeThreshold}
      maxInfantAge={infantAgeThreshold}
      capacities={capacities}
      data-testid="OfferCapacitySelect"
      className={cn(className, 'offer-capacity-select')}
      disclaimer={disclaimer}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoomTypesEvents={enableMultiRoomTypesEvents}
      enableMultiRoom={!disableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      showChildren={!isLETour}
      offerType={offer.type}
      offerId={offer.id}
    />
  )
}

export default withOfferPageAndReduxState(
  (state, reduxProps) => ({
    bundleOfferId: state.bundleOffer?.id,
    rooms: state.rooms,
    offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
    open: state.activeFilter === 'capacity',
    filter: state.activeFilter,
    interactionStudioData: getInteractionStudioDataFromOfferPage(reduxProps, state),
  }))(OfferCapacityWithAgesSelection)
